"use client";

import { cn } from "@packages/ui/lib/utils";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

interface AnimatedThemeTogglerProps
	extends React.ComponentPropsWithoutRef<"button"> {
	duration?: number;
}

export function AnimatedThemeToggler({
	className,
	duration = 400,
	...props
}: AnimatedThemeTogglerProps) {
	const { resolvedTheme, setTheme } = useTheme();
	const [mounted, setMounted] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	const isDark = resolvedTheme === "dark";

	const toggleTheme = useCallback(() => {
		const button = buttonRef.current;
		if (!button) return;

		const { top, left, width, height } = button.getBoundingClientRect();
		const x = left + width / 2;
		const y = top + height / 2;
		const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
		const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
		const maxRadius = Math.hypot(
			Math.max(x, viewportWidth - x),
			Math.max(y, viewportHeight - y),
		);

		const applyTheme = () => {
			setTheme(isDark ? "light" : "dark");
		};

		if (typeof document.startViewTransition !== "function") {
			applyTheme();
			return;
		}

		const transition = document.startViewTransition(() => {
			flushSync(applyTheme);
		});

		const ready = transition?.ready;
		if (ready && typeof ready.then === "function") {
			ready.then(() => {
				document.documentElement.animate(
					{
						clipPath: [
							`circle(0px at ${x}px ${y}px)`,
							`circle(${maxRadius}px at ${x}px ${y}px)`,
						],
					},
					{
						duration,
						easing: "ease-in-out",
						pseudoElement: "::view-transition-new(root)",
					},
				);
			});
		}
	}, [isDark, duration, setTheme]);

	if (!mounted) {
		return (
			<button
				type="button"
				className={cn("size-8 rounded-md", className)}
				{...props}
			/>
		);
	}

	return (
		<button
			type="button"
			ref={buttonRef}
			onClick={toggleTheme}
			className={cn(
				"rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground",
				className,
			)}
			{...props}
		>
			{isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
			<span className="sr-only">Toggle theme</span>
		</button>
	);
}
